import { queryClient as defaultQueryClient } from '@/plugins/vueQuery';
import { queryKeys } from '../constants';
import type { BudgetTabName } from '@/types/budgets.categorized';
import type { ApiGetTeamBudgetGroupsCompactParams } from '@/types/budgetGroups';
import type { QueryInvalidationParams } from '@/types/query';
import type { NilCategoryType } from '@/types/nilCategories';

const MODULE_KEY = 'budgets';

/** Query Key Getters */

export const getTeamBudgetsQueryKey = (teamTwrn?: string | null) => {
  const baseKey = [MODULE_KEY, queryKeys.TEAM_BUDGETS];

  if (teamTwrn) {
    return [...baseKey, teamTwrn];
  }

  return baseKey;
};

export const getTeamBudgetQueryKey = (teamTwrn?: string | null, budgetTwrn?: string | null) => {
  const key = [MODULE_KEY, queryKeys.TEAM_BUDGET_DETAILS];

  if (teamTwrn) {
    key.push(teamTwrn);
  }

  if (budgetTwrn) {
    if (!teamTwrn) {
      console.warn(
        '[getTeamBudgetQueryKey] budgetTwrn was provided without teamTwrn. teamTwrn is required when budgetTwrn is passed.',
        {
          budgetTwrn,
          teamTwrn,
        },
      );
      return key;
    }
    key.push(budgetTwrn);
  }

  return key;
};


export const getOrgBudgetsListQueryKey = (requestBody?: {
  cursor?: string | null;
  per_page?: number;
}, filtersReady?: boolean) => {
  const key: any[] = [MODULE_KEY, queryKeys.ORG_BUDGETS];

  if (requestBody) {
    key.push(requestBody);
  }

  if (filtersReady) {
    if (!requestBody) {
      console.warn(
        '[getOrgBudgetsListQueryKey] filtersReady was provided without requestBody. requestBody is required when filtersReady is passed.',
        {
          filtersReady,
          requestBody,
        },
      );
      return key;
    }
    key.push(filtersReady);
  }

  return key;
};

export const getOrgBudgetsOverviewQueryKey = (orgTwrn?: string | null, academicYearUuid?: string | null) => {
  const key = [MODULE_KEY, queryKeys.ORG_BUDGETS_SUMMARY];

  if (orgTwrn) {
    key.push(orgTwrn);
  }

  if (academicYearUuid) {
    if (!orgTwrn) {
      console.warn(
        '[getOrgBudgetsOverviewQueryKey] academicYearUuid was provided without orgTwrn. orgTwrn is required when academicYearUuid is passed.',
        {
          academicYearUuid,
          orgTwrn,
        },
      );
      return key;
    }
    key.push(academicYearUuid);
  }

  return key;
};

export const getTeamBudgetColumnPreferencesQueryKey = (teamTwrn?: string | null, budgetTwrn?: string | null, tabName?: BudgetTabName | null) => {
  const key = [MODULE_KEY, 'column-preferences', queryKeys.TEAM_BUDGET_COLUMN_PREFERENCES];

  if (teamTwrn) {
    key.push(teamTwrn);
  }

  if (budgetTwrn) {
    if (!teamTwrn) {
      console.warn(
        '[getTeamBudgetCategorizedQueryKey] budgetTwrn was provided without teamTwrn. teamTwrn is required when budgetTwrn is passed.',
        {
          budgetTwrn,
          teamTwrn,
        },
      );
      return key;
    }
    key.push(budgetTwrn);
  }

  if (tabName) {
    key.push(tabName);
  }

  return key;
};

/**
 * Cache key for a budget group's combined column preferences (one query
 * covering every budget in the group), keyed by `(budgetGroupTwrn, tabName)`.
 *
 * This is distinct from `getTeamBudgetColumnPreferencesQueryKey`, which is
 * scoped to a single `(teamTwrn, budgetTwrn, tabName)` — that getter reads
 * one budget's preferences, this one reads every budget in a group at once.
 */
export const getTeamBudgetGroupColumnPreferencesQueryKey = (budgetGroupTwrn?: string | null, tabName?: BudgetTabName | null) => {
  const key = [MODULE_KEY, queryKeys.TEAM_BUDGET_GROUP_COLUMN_PREFERENCES];

  if (budgetGroupTwrn) {
    key.push(budgetGroupTwrn);
  }

  if (tabName) {
    if (!budgetGroupTwrn) {
      console.warn(
        '[getTeamBudgetGroupColumnPreferencesQueryKey] tabName was provided without budgetGroupTwrn. budgetGroupTwrn is required when tabName is passed.',
        {
          tabName,
          budgetGroupTwrn,
        },
      );
      return key;
    }
    key.push(tabName);
  }

  return key;
};

export const getBudgetGroupsCompactQueryKey = (params?: Omit<ApiGetTeamBudgetGroupsCompactParams, 'teams'> & { teams?: (string | null)[] }) => {
  const key: unknown[] = [MODULE_KEY, queryKeys.BUDGET_GROUPS_COMPACT];

  if (params) {
    key.push(params);
  }

  return key;
};

export const getBudgetGroupQueryKey = (teamTwrn?: string | null, budgetGroupTwrn?: string | null) => {
  const key = [MODULE_KEY, queryKeys.BUDGET_GROUP];

  if (teamTwrn) {
    key.push(teamTwrn);
  }

  if (budgetGroupTwrn) {
    if (!teamTwrn) {
      console.warn(
        '[getBudgetGroupQueryKey] budgetGroupTwrn was provided without teamTwrn. teamTwrn is required when budgetGroupTwrn is passed.',
        {
          budgetGroupTwrn,
          teamTwrn,
        },
      );
      return key;
    }
    key.push(budgetGroupTwrn);
  }

  return key;
};

export const getNilCategoriesQueryKey = (teamTwrn?: string | null, type?: NilCategoryType | null) => {
  const key = [MODULE_KEY, queryKeys.NIL_CATEGORIES];

  if (teamTwrn) {
    key.push(teamTwrn);
  }

  if (type) {
    if (!teamTwrn) {
      console.warn(
        '[getNilCategoriesQueryKey] type was provided without teamTwrn. teamTwrn is required when type is passed.',
        {
          type,
          teamTwrn,
        },
      );
      return key;
    }
    key.push(type);
  }

  return key;
};

/** Invalidation Helpers */

export const invalidateOrgBudgets = (
  options?: QueryInvalidationParams & { invalidateOverview?: boolean },
) => {
  const client = options?.queryClient ?? defaultQueryClient;

  client.invalidateQueries({ queryKey: getOrgBudgetsListQueryKey() });

  if (options?.invalidateOverview) {
    client.invalidateQueries({ queryKey: getOrgBudgetsOverviewQueryKey() });
  }
};

export const invalidateTeamBudgets = (
  teamTwrn?: string | null,
  budgetTwrn?: string | null,
  options?: QueryInvalidationParams,
) => {
  const client = options?.queryClient ?? defaultQueryClient;

  client.invalidateQueries({ queryKey: getTeamBudgetsQueryKey(teamTwrn) });

  if (budgetTwrn) {
    client.invalidateQueries({ queryKey: getTeamBudgetQueryKey(teamTwrn, budgetTwrn) });
  }
};

/**
 * Invalidates org + team budget listings, and optionally the org budgets overview.
 *
 * `invalidateOverview` defaults to `false` to preserve existing callers that only care
 * about listing rows. Pass `{ invalidateOverview: true, orgTwrn }` when the mutation also
 * affects overview totals (e.g. contracted dollars).
 */
export const invalidateBudgetListings = (
  teamTwrn?: string | null,
  options?: QueryInvalidationParams & {
    orgTwrn?: string | null;
    invalidateOverview?: boolean;
  },
) => {
  const client = options?.queryClient ?? defaultQueryClient;

  client.invalidateQueries({ queryKey: getOrgBudgetsListQueryKey() });
  client.invalidateQueries({ queryKey: getTeamBudgetsQueryKey(teamTwrn) });

  if (options?.invalidateOverview) {
    client.invalidateQueries({ queryKey: getOrgBudgetsOverviewQueryKey(options.orgTwrn) });
  }
};
